import React, {useEffect, useState} from 'react';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

import {Select} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Space} from 'src/design/atoms/Space';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {DateRangePicker} from 'src/design/atoms/DateRangePicker';
import {Description} from 'src/design/atoms/Description';
import {Subtitle} from 'src/design/atoms/Typography';
import {Spinner} from 'src/design/atoms/Spinner';
import {useGetCourseDefinitionQuizStatisticsQuery} from 'src/redux/queries/courseDefinition';
import {getLanguageDisplayName} from 'src/app/training/languageUtils';
import {toUtcDayRange} from 'src/shared/utils/utcDate';

interface CourseQuizResultsTabProps {
  courseId: number;
}

export const CourseQuizResultsTab: React.FC<CourseQuizResultsTabProps> = ({
  courseId,
}) => {
  const {setAlert} = useAlert();
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [initialized, setInitialized] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  const {data, isLoading, isFetching, error} =
    useGetCourseDefinitionQuizStatisticsQuery(
      {id: courseId, language: selectedLanguage, ...toUtcDayRange(dateRange)},
      {skip: !courseId}
    );

  useEffect(() => {
    if (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [error]);

  useEffect(() => {
    if (!data) return;
    if (!initialized) {
      setInitialized(true);
      if (data.defaultLanguage !== selectedLanguage) {
        setSelectedLanguage(data.defaultLanguage);
      }
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading && !data) {
    return <Spinner loadingText="Loading Quiz Results..." />;
  }

  const isMultiLanguage = !!data && data.availableLanguages.length > 1;
  const hasDateRange = dateRange[0] !== null || dateRange[1] !== null;

  if (
    !data ||
    (data.quizzes.length === 0 && !isMultiLanguage && !hasDateRange)
  ) {
    return <Text>No quiz statistics available for this course.</Text>;
  }

  return (
    <>
      <Subtitle text="Quiz Results" />
      <Description
        size="sm"
        text="An aggregated report of any quizzes in this training. Only quizzes which are in the current version of the training are shown. If a question or answer was changed between versions, the previous version of the question is not shown. If no answers have been submitted for the quiz yet, it will not be shown. Use the completed date filter to limit results to quizzes completed in a date range."
      />
      <Space mb="md" />

      <Group mb="md" align="flex-end">
        {isMultiLanguage && (
          <Select
            label="Quiz Language"
            data={data.availableLanguages.map(code => ({
              value: code,
              label: `${getLanguageDisplayName(code)}${
                code === data.defaultLanguage ? ' (Default)' : ''
              }`,
            }))}
            value={selectedLanguage}
            onChange={val => val && setSelectedLanguage(val)}
            allowDeselect={false}
            style={{minWidth: 200}}
          />
        )}
        <DateRangePicker
          label="Completed Date"
          placeholder="All dates"
          valueFormat="MMM D, YYYY"
          clearable
          value={dateRange}
          onChange={setDateRange}
          w={240}
        />
      </Group>

      {isFetching ? (
        <Spinner loadingText="Loading Quiz Results..." />
      ) : data.quizzes.length === 0 ? (
        <Text>
          {hasDateRange
            ? 'No quiz results were completed in the selected date range.'
            : 'No quiz statistics available for this language.'}
        </Text>
      ) : (
        data.quizzes.map(quiz => (
          <Box key={quiz.quizId} mb="xl">
            <Title order={4} mb="sm">
              {quiz.quizName} Results
            </Title>

            {quiz.questions.map((question, index) => (
              <Box key={question.questionId} mb="md">
                <Title order={5} mb="sm">
                  {index + 1}. {question.questionText}
                </Title>
                <Group align="flex-start" gap="xs">
                  <Text>Correct Answers:</Text>
                  <Text>{question.percentageCorrect.toFixed(0)}%</Text>
                </Group>
                <Group align="flex-start" gap="xs">
                  <Text>Count of Correct Answers in Total Answers:</Text>
                  <Text>
                    {question.correctAnswerCount}/{question.totalAnswerCount}
                  </Text>
                </Group>
              </Box>
            ))}
          </Box>
        ))
      )}
    </>
  );
};

export default CourseQuizResultsTab;
